import * as React from 'react';

import {
  Box,
  ButtonGroup,
  IconButton,
  Paper,
  Popper,
} from '@mui/material';

import EditIcon from '@mui/icons-material/Edit';
import EditOffIcon from '@mui/icons-material/EditOff';
import SaveIcon from '@mui/icons-material/Save';

import _uniqueId from 'lodash/uniqueId';
import { useFormContext } from 'react-hook-form';
import { useFormValidation } from '../core/context/FormValidationContext';

interface IEditableGroup {
  fieldNames: string[]
  disableEditPopper?: boolean,
  renderMode: 'read' | 'edit',
  renderEditFields: () => React.ReactElement,
  renderReadFields: () => React.ReactElement,
  saveSuccess: () => void,
  onEditCancel: () => void,
  activeIndex: number,
}
function EditableGroup({
  fieldNames,
  disableEditPopper,
  renderMode,
  renderEditFields,
  renderReadFields,
  saveSuccess,
  onEditCancel,
  activeIndex,
}: IEditableGroup): JSX.Element {
  const [popperId] = React.useState(_uniqueId('forms-editable-popper-'));
  const [isDirty, setIsDirty] = React.useState(false);
  const [internalRenderMode, setInternalRenderMode] = React.useState<'read' | 'edit'>(renderMode);
  const [popperOpen, setPopperOpen] = React.useState(false);
  const [popperAnchorEl, setPopperAnchorEl] = React.useState<HTMLElement | null>(null);
  const { activeControl, setActiveControl } = useFormValidation();
  const { formState } = useFormContext();
  const handleEnterEditMode = () => {
    setActiveControl(fieldNames[activeIndex]);
    setInternalRenderMode('edit');
  };
  const handleExitEditMode = () => {
    onEditCancel();
    setInternalRenderMode('read');
    setActiveControl('');
  };

  const requestEditSave = () => {
    saveSuccess();
    setInternalRenderMode('read');
    setActiveControl('');
  };

  const handleMouseOver = (event: React.MouseEvent<HTMLElement>) => {
    setPopperAnchorEl(event.currentTarget);
    setPopperOpen(true);
  };

  const handleMouseOut = (event: React.MouseEvent<HTMLElement>) => {
    const popper = document.getElementById(popperId);
    const relatedTarget = event.relatedTarget as HTMLElement | null;

    if (popper !== null && relatedTarget !== null) {
      if (!(relatedTarget === popper || popper.contains(relatedTarget))) {
        setPopperAnchorEl(null);
        setPopperOpen(false);
      }
    }
  };
  React.useEffect(() => {
    const fieldIsDirty = fieldNames.some((fieldName) => fieldName in formState.dirtyFields);
    setIsDirty(fieldIsDirty);
  }, [formState]);
  if (renderMode === 'edit') return <React.Fragment>{renderEditFields()}</React.Fragment>;
  if (disableEditPopper || (activeControl !== '' && !fieldNames.includes(activeControl ?? ''))) {
    return <React.Fragment>{renderReadFields()}</React.Fragment>;
  }
  return (
    <Box
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
      sx={{ width: '100%' }}
    >
      <Popper
        id={popperId}
        open={popperOpen}
        anchorEl={popperAnchorEl}
        placement="right"
        modifiers={[
          {
            name: 'offset',
            options: {
              // [Popper Offset Modifier Documentation](https://popper.js.org/docs/v2/modifiers/offset/)
              // There are no TypeScript typings for these options, unfortunately.
              offset: (o: any) => [0, 0 - o.popper.width - 8],
            },
          },
        ]}
      >
        <Paper>
          <ButtonGroup size="small">
            {internalRenderMode === 'read' && (
              <IconButton color="primary" onClick={handleEnterEditMode}>
                <EditIcon />
              </IconButton>
            )}
            {internalRenderMode === 'edit' && (
              <React.Fragment>
                <IconButton color="primary" onClick={requestEditSave}>
                  <SaveIcon />
                </IconButton>
                <IconButton color="secondary" onClick={handleExitEditMode}>
                  <EditOffIcon />
                </IconButton>
              </React.Fragment>
            )}
          </ButtonGroup>
        </Paper>
      </Popper>
      <div className={isDirty && renderMode === 'read' ? 'dirtyField' : ''}>
        {internalRenderMode === 'read'
          ? renderReadFields()
          : renderEditFields()}
      </div>
    </Box>
  );
}

export default EditableGroup;
